import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../../context/AuthContext';
import { decodeJWT, isAdmin } from '../../utils/jwtUtils';
import { 
  Container, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner,
  InputGroup,
  FormControl,
  Badge,
  Row,
  Col,
  Card
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaTimes } from 'react-icons/fa';
import { organizationService } from '../../services/api';

const OrganizationManagement = () => {
  const { currentUser } = useAuth();
  const token = localStorage.getItem('token');
  
  // Afficher des informations de débogage sur le token
  useEffect(() => {
    if (token) {
      const decoded = decodeJWT(token);
      console.log('=== Informations du token JWT ===');
      console.log('Token décodé:', decoded);
      console.log('Utilisateur est admin:', isAdmin(token));
      console.log('Utilisateur courant:', currentUser);
    } else {
      console.warn('Aucun token JWT trouvé dans le localStorage');
    }
  }, [token, currentUser]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Charger les organisations
  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await organizationService.getOrganizations({ 
        name: searchTerm || undefined,
        isActive: true 
      });
      setOrganizations(response.data);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des organisations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, [searchTerm]);

  // Gérer les changements du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Ouvrir la modal d'ajout
  const handleAddClick = () => {
    setEditingOrg(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  // Ouvrir la modal d'édition
  const handleEditClick = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      description: org.description || ''
    });
    setShowModal(true);
  };

  // Soumettre le formulaire (ajout ou édition)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingOrg) {
        // Mise à jour d'une organisation existante
        await organizationService.updateOrganization(editingOrg._id, formData);
        setSuccess('Organisation mise à jour avec succès');
      } else {
        // Création d'une nouvelle organisation
        await organizationService.createOrganization(formData);
        setSuccess('Organisation créée avec succès');
      }
      
      // Recharger la liste et fermer la modal
      await loadOrganizations();
      setShowModal(false);
      
      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  // Supprimer une organisation (soft delete)
  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette organisation ?')) {
      try {
        await organizationService.deleteOrganization(id);
        setSuccess('Organisation supprimée avec succès');
        await loadOrganizations();
        
        // Effacer le message de succès après 3 secondes
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.message || 'Une erreur est survenue lors de la suppression');
      }
    }
  };

  // Recherche d'organisations
  const handleSearch = (e) => {
    e.preventDefault();
    loadOrganizations();
  };

  // Réinitialiser la recherche
  const resetSearch = () => {
    setSearchTerm('');
    loadOrganizations();
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Gestion des Organisations</h2>
          <p className="text-muted">Gérez les organisations disponibles pour les profils</p>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={handleAddClick}>
            <FaPlus className="me-2" />
            Ajouter une organisation
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-4">
            <InputGroup>
              <FormControl
                placeholder="Rechercher une organisation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button variant="outline-secondary" onClick={resetSearch}>
                  <FaTimes />
                </Button>
              )}
              <Button variant="primary" type="submit">
                <FaSearch className="me-1" /> Rechercher
              </Button>
            </InputGroup>
          </Form>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </Spinner>
            </div>
          ) : organizations.length === 0 ? (
            <Alert variant="info">
              Aucune organisation trouvée
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org) => (
                    <tr key={org._id}>
                      <td>
                        <strong>{org.name}</strong>
                      </td>
                      <td>
                        {org.description || <span className="text-muted">Aucune description</span>}
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="me-2"
                          onClick={() => handleEditClick(org)}
                        >
                          <FaEdit />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(org._id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal d'ajout/édition */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingOrg ? 'Modifier une organisation' : 'Ajouter une organisation'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom de l'organisation *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Ex: Université du Québec à Rimouski"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Description de l'organisation (optionnel)"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit">
              {editingOrg ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default OrganizationManagement;
